import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomCommandBar from "../../Components/CustomCommandBar/CustomCommandBar";
import NavigationMenu from "../../Components/NavigationMenu/NavigationMenu";
import { SummonModal } from "../../Components/SmartModal/SmartModal";
import TestAlarmDialog, { TEST_ALARM_MODAL_ID } from "../../Components/TestAlarmDialog/TestAlarmDialog";
import { AppRoutes } from "../../Constants/AppRoutes";
import { ICaring } from "../../Models/ICaring";
import { IRole } from "../../Models/IRole";
import { ISentinelRich } from "../../Models/ISentinel";
import { IUser } from "../../Models/IUser";
import { GlobalState } from "../../Redux/RootReducer";
import { IAPIResponse } from "../../Services/AjaxService";
import ApiService from "../../Services/ApiService";
import { ToastMessage } from "../../Utils/UIMessages";
import DevicesTab from "./DevicesTab";
import NotificationsTab from "./NotificationsTab";
import { CARDS_MAX_WIDTH, cx, useWidth } from "./SetupUi";
import UsersTab from "./UsersTab";
import "./SetupPage.scss";

// Setup (mockup-setup-v2, approvato il 24/10/2026): schede Dispositivi, Operatori, Notifiche (nomi del 01/11/2026).
// Carings e Sensori non sono più schede: operatori, sensori e telecamera si cambiano dalla riga
// del dispositivo; in Utenti «Assegnato a» dice i dispositivi di ogni operatore.

const SETUP_TABS = [
  { key: "dispositivo", text: "Dispositivi" },
  { key: "utenti", text: "Operatori" },
  { key: "notifiche", text: "Notifiche" },
];

const SetupPage = () => {
  const navigate = useNavigate();
  const width = useWidth();
  const loggedUser = useSelector((s: GlobalState) => s.generic.user);
  const [tab, setTab] = useState<string>("dispositivo");
  const [sentinels, setSentinels] = useState<ISentinelRich[]>([]);
  const [users, setUsers] = useState<IUser[]>([]);
  const [roles, setRoles] = useState<IRole[]>([]);
  const [carings, setCarings] = useState<ICaring[]>([]);
  const [loaded, setLoaded] = useState<Record<string, boolean>>({});
  const [testSentinel, setTestSentinel] = useState<ISentinelRich | undefined>();
  const [testRequestId, setTestRequestId] = useState<number>(0);

  const done = (key: string) => setLoaded((prev) => ({ ...prev, [key]: true }));

  const loadSentinels = (fresh = true) => {
    ApiService.SentinelController.GetSentinelsAll((response: IAPIResponse) => {
      if (response.error === null) setSentinels(response.parsed);
      else ToastMessage("error", "Impossibile caricare i dispositivi.");
      done("sentinels");
    }, fresh);
  };
  const loadUsers = (fresh = true) => {
    ApiService.UsersController.GetUsers((response: IAPIResponse) => {
      if (response.error === null) setUsers(response.parsed);
      else ToastMessage("error", "Impossibile caricare gli utenti.");
      done("users");
    }, fresh);
  };
  const loadRoles = () => {
    ApiService.UsersController.GetUsersRoles((response: IAPIResponse) => {
      if (response.error === null) setRoles(response.parsed);
      else ToastMessage("error", "Impossibile caricare i ruoli.");
    });
  };
  const loadCarings = (fresh = true) => {
    ApiService.CaringController.GetCarings((response: IAPIResponse) => {
      if (response.error === null) setCarings(response.parsed);
      else ToastMessage("error", "Impossibile caricare gli operatori assegnati.");
      done("carings");
    }, fresh);
  };

  useEffect(() => {
    // all'apertura va bene la copia in cache (poi arrivano i dati nuovi); dopo una modifica no
    loadSentinels(false);
    loadUsers(false);
    loadRoles();
    loadCarings(false);
  }, []);

  const goToConfigure = (sentinelId: number) => {
    navigate(AppRoutes.APP_CONFIGURE_PAGE + "?sentinelId=" + sentinelId);
    setTimeout(() => {
      window.document.dispatchEvent(new Event("reset-navigation"));
    }, 200);
  };

  const openTest = (s: ISentinelRich) => {
    setTestSentinel(s);
    setTestRequestId(testRequestId + 1);
    SummonModal(TEST_ALARM_MODAL_ID);
  };

  const cards = width < CARDS_MAX_WIDTH;
  const allLoaded = !!(loaded.sentinels && loaded.users && loaded.carings);

  return (
    <div className="fade-in-whole-page">
      <CustomCommandBar specials={[<div className="command-bar-title">Admin Center</div>, <NavigationMenu />]} buttons={[]} />
      <div className="sliding-page-content setup2-scroll">
        <div className={cx("setup2", "setup2-page", cards && "w-cards")}>
          {cards ? (
            <div className="tabsel">
              <label htmlFor="setup-tab">Scheda</label>
              <select id="setup-tab" className="tf" value={tab} onChange={(e) => setTab(e.target.value)}>
                {SETUP_TABS.map((x) => (
                  <option key={x.key} value={x.key}>
                    {x.text}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="tabs" role="tablist" aria-label="Schede di Setup">
              {SETUP_TABS.map((x) => (
                <button
                  type="button"
                  key={x.key}
                  role="tab"
                  aria-selected={tab === x.key}
                  className={cx("tab", tab === x.key && "sel")}
                  onClick={() => setTab(x.key)}
                >
                  {x.text}
                </button>
              ))}
            </div>
          )}
          {tab === "dispositivo" && (
            <DevicesTab
              width={width}
              sentinels={sentinels}
              users={users}
              carings={carings}
              loaded={allLoaded}
              reloadSentinels={() => loadSentinels()}
              reloadCarings={() => loadCarings()}
              onConfigure={goToConfigure}
              onTest={openTest}
            />
          )}
          {tab === "utenti" && (
            <UsersTab
              width={width}
              users={users}
              roles={roles}
              sentinels={sentinels}
              carings={carings}
              loaded={allLoaded}
              meId={loggedUser?.id}
              reloadUsers={() => loadUsers()}
              onRelogin={() => navigate(AppRoutes.APP_LOGOUT_PAGE)}
            />
          )}
          {tab === "notifiche" && <NotificationsTab width={width} />}
        </div>
      </div>
      <TestAlarmDialog
        requestId={testRequestId}
        sentinel={testSentinel}
        onOpenNotifications={() => setTab("notifiche")}
        onClosed={() => loadSentinels()}
      />
    </div>
  );
};

export default SetupPage;
